/**
 * CameraModal Component
 * 
 * A full-screen camera preview modal for desktop browsers.
 * Provides live camera feed with capture, flip camera, and cancel functionality.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  Close,
  CameraAlt,
  FlipCameraAndroid,
  Replay,
  Check,
} from '@mui/icons-material';
import { hasMultipleCameras } from '../utils/deviceDetection';

interface CameraModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageData: string, preview: string) => void;
}

type CameraState = 'initializing' | 'ready' | 'error' | 'preview';

const CameraModal: React.FC<CameraModalProps> = ({ open, onClose, onCapture }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [cameraState, setCameraState] = useState<CameraState>('initializing');
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [showFlipButton, setShowFlipButton] = useState(false);
  const [capturedImage, setCapturedImage] = useState<{ data: string; preview: string } | null>(null);

  /**
   * Stop the camera stream and clean up resources
   */
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  /**
   * Initialize the camera stream
   */
  const initializeCamera = useCallback(async () => {
    console.log('CameraModal: Starting camera initialization...');
    setCameraState('initializing');
    setError(null);
    
    // Stop any existing stream first
    stopStream();
    
    try {
      // Check for camera support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported in this browser');
      }
      
      console.log('CameraModal: Requesting camera access...');
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      
      console.log('CameraModal: Camera stream obtained', stream.getVideoTracks());
      
      streamRef.current = stream;
      
      // Wait a tick to ensure video element is mounted
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (videoRef.current) {
        console.log('CameraModal: Attaching stream to video element');
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready with timeout
        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current;
          if (!video) {
            reject(new Error('Video element not found'));
            return;
          }
          
          const timeoutId = setTimeout(() => {
            console.log('CameraModal: Metadata timeout, attempting to play anyway');
            resolve();
          }, 3000);
          
          // If metadata is already loaded
          if (video.readyState >= 1) {
            console.log('CameraModal: Video already has metadata');
            clearTimeout(timeoutId);
            resolve();
            return;
          }
          
          video.onloadedmetadata = () => {
            console.log('CameraModal: Video metadata loaded');
            clearTimeout(timeoutId);
            resolve();
          };
          
          video.onerror = (e) => {
            console.error('CameraModal: Video error', e);
            clearTimeout(timeoutId);
            reject(new Error('Video failed to load'));
          };
        });
        
        console.log('CameraModal: Playing video...');
        await videoRef.current.play();
        console.log('CameraModal: Video playing successfully');
      } else {
        console.error('CameraModal: Video element ref is null');
        throw new Error('Video element not available');
      }
      
      setCameraState('ready');
      console.log('CameraModal: Camera ready!');
      
      // Check if flip button should be shown
      const multiCam = await hasMultipleCameras();
      setShowFlipButton(multiCam);
      
    } catch (err) {
      console.error('Camera initialization error:', err);
      
      let errorMessage = 'Failed to access camera';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = 'Camera permission denied. Please allow camera access in your browser settings.';
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = 'No camera found. Please connect a camera and try again.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = 'Camera is in use by another application. Please close other apps using the camera.';
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = 'Camera does not support the requested settings.';
        } else {
          errorMessage = err.message || 'Failed to access camera';
        }
      }
      
      setError(errorMessage);
      setCameraState('error');
    }
  }, [facingMode, stopStream]);

  /**
   * Flip between front and back camera
   */
  const handleFlipCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  /**
   * Capture the current video frame
   */
  const handleCapture = useCallback(() => {
    if (!videoRef.current || cameraState !== 'ready') return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement('canvas');
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Failed to capture image');
      return;
    }
    
    // Draw the current video frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to JPEG
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const base64Data = dataUrl.split(',')[1];
    
    // Store captured image for preview
    setCapturedImage({ data: base64Data, preview: dataUrl });
    setCameraState('preview');
  }, [cameraState]);

  /**
   * Retake photo - go back to live view
   */
  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    setCameraState('ready');
  }, []);

  /**
   * Confirm and use the captured photo
   */
  const handleUsePhoto = useCallback(() => {
    if (capturedImage) {
      onCapture(capturedImage.data, capturedImage.preview);
      handleClose();
    }
  }, [capturedImage, onCapture]);

  /**
   * Close the modal and clean up
   */
  const handleClose = useCallback(() => {
    stopStream();
    setCapturedImage(null);
    setCameraState('initializing');
    setError(null);
    onClose();
  }, [stopStream, onClose]);

  // Track if we're in preview mode to avoid re-initializing camera
  const isInPreviewMode = useRef(false);
  
  // Update ref when cameraState changes
  useEffect(() => {
    isInPreviewMode.current = cameraState === 'preview';
  }, [cameraState]);

  // Initialize camera when modal opens or facing mode changes
  useEffect(() => {
    if (open && !isInPreviewMode.current) {
      initializeCamera();
    }
  }, [open, facingMode, initializeCamera]);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!open) {
      stopStream();
      setCapturedImage(null);
      setCameraState('initializing');
      isInPreviewMode.current = false;
    }
    
    return () => {
      stopStream();
    };
  }, [open, stopStream]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#000',
          height: isSmallScreen ? '100%' : '80vh',
          maxHeight: '800px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      {/* Main content area */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
          aria-label="Close camera"
        >
          <Close />
        </IconButton>

        {/* Video/Preview area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Live video feed - always render but hide when not ready or in preview mode */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: cameraState === 'ready' ? 'block' : 'none',
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none', // Mirror front camera
            }}
          />

          {/* Captured image preview */}
          {cameraState === 'preview' && capturedImage && (
            <img
              src={capturedImage.preview}
              alt="Captured"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          )}

          {/* Loading state */}
          {cameraState === 'initializing' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: '#fff' }} />
              <Typography sx={{ color: '#fff' }}>
                Initializing camera...
              </Typography>
            </Box>
          )}

          {/* Error state */}
          {cameraState === 'error' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: '#f44336', fontSize: '3rem' }}>
                📷
              </Typography>
              <Typography sx={{ color: '#fff', maxWidth: 400 }}>
                {error}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={{ color: '#fff', borderColor: '#fff' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={initializeCamera}
                  sx={{ bgcolor: '#1976d2' }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Control bar */}
        {(cameraState === 'ready' || cameraState === 'preview') && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              py: 3,
              px: 2,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
            }}
          >
            {cameraState === 'ready' ? (
              <>
                {/* Flip camera button (left) */}
                <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                  {showFlipButton && (
                    <Tooltip title="Flip camera">
                      <IconButton
                        onClick={handleFlipCamera}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                          },
                        }}
                        aria-label="Flip camera"
                      >
                        <FlipCameraAndroid />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Capture button (center) */}
                <Tooltip title="Take photo">
                  <Fab
                    onClick={handleCapture}
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: '#fff',
                      color: '#333',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      border: '4px solid rgba(255, 255, 255, 0.5)',
                    }}
                    aria-label="Take photo"
                  >
                    <CameraAlt sx={{ fontSize: 32 }} />
                  </Fab>
                </Tooltip>

                {/* Spacer (right) */}
                <Box sx={{ width: 56 }} />
              </>
            ) : (
              <>
                {/* Retake button */}
                <Tooltip title="Retake photo">
                  <IconButton
                    onClick={handleRetake}
                    sx={{
                      color: '#fff',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 56,
                      height: 56,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                    aria-label="Retake photo"
                  >
                    <Replay sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>

                {/* Use photo button */}
                <Tooltip title="Use this photo">
                  <Fab
                    onClick={handleUsePhoto}
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: '#4caf50',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#388e3c',
                      },
                    }}
                    aria-label="Use this photo"
                  >
                    <Check sx={{ fontSize: 32 }} />
                  </Fab>
                </Tooltip>

                {/* Spacer */}
                <Box sx={{ width: 56 }} />
              </>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CameraModal;
